import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Avatar, IconButton, Menu, MenuItem, Divider, Modal, Backdrop, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import {
  ArrowBack,
  MoreVert,
  Search as SearchIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Block as BlockIcon,
  Report as ReportIcon,
  NotificationsOff as MuteIcon,
  Notifications as UnmuteIcon,
  Inventory2 as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import capitalizeEachFirstWord from "../../../utils/CapLetters";
import useApp from "../../../store/contexts/AppContext";
import formatLastSeen from "../../../utils/formatLastSeen";
import { ROUTES } from "../../../routes/User/paths";
import { CHAT_ROOMS } from "../../../config";
import ChatSettings from "./ChatSettings";

// Styled Components
const HeaderContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: { xs: "68px", sm: "76px" },
  minHeight: { xs: "68px", sm: "76px" },
  maxHeight: { xs: "68px", sm: "76px" },
  // padding: { xs: "12px 20px", sm: "16px 28px" },
  padding: "15px 10px",
  background: theme.palette.mode === "dark"
    ? `linear-gradient(to bottom,
        ${alpha("#0f1419", 0.98)},
        ${alpha("#0a0e13", 1)})`
    : `linear-gradient(to bottom,
        ${alpha("#ffffff", 0.98)},
        ${alpha("#f8f9fa", 1)})`,
  backdropFilter: "blur(24px)",
  borderBottom: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.06)
      : alpha("#000", 0.08)
  }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  zIndex: 10,
  flexShrink: 0,
  boxShadow: theme.palette.mode === "dark"
    ? `0 4px 20px ${alpha("#000", 0.25)}`
    : `0 2px 12px ${alpha("#000", 0.08)}`,

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: { xs: "85%", sm: "75%" },
    height: "1px",
    background: `linear-gradient(90deg,
      transparent,
      ${alpha(theme.palette.primary.main, 0.4)} 20%,
      ${alpha(theme.palette.primary.main, 0.6)} 50%,
      ${alpha(theme.palette.primary.main, 0.4)} 80%,
      transparent)`,
  },
}));

const LeftSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: { xs: "14px", sm: "18px" },
  flex: 1,
  minWidth: 0,
});

const RightSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  paddingRight: "8px",
});

const BackButton = styled(IconButton)(({ theme }) => ({
  display: { xs: "inline-flex", sm: "none" },
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.9) : alpha("#000", 0.8),
  padding: "10px",
  marginLeft: "-8px",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    transform: "scale(1.05)",
  },

  "&:active": {
    transform: "scale(0.95)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: { xs: "22px", sm: "24px" },
  },
}));

const SearchButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.7) : alpha("#000", 0.6),
  padding: "10px",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    transform: "scale(1.05)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: { xs: "20px", sm: "22px" },
  },
}));

const AvatarWrapper = styled(Box)(({ theme, isOnline }) => ({
  position: "relative",
  cursor: "pointer",

  "&::after": isOnline ? {
    content: '""',
    position: "absolute",
    bottom: "2px",
    right: "2px",
    width: { xs: "10px", sm: "12px" },
    height: { xs: "10px", sm: "12px" },
    borderRadius: "50%",
    background: "#4CAF50",
    border: `2px solid ${
      theme.palette.mode === "dark" ? alpha("#0a0e13", 1) : alpha("#ffffff", 1)
    }`,
    boxShadow: `0 0 12px ${alpha("#4CAF50", 0.8)}`,
    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  } : {},

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.8,
      transform: "scale(1.05)",
    },
  },
}));


const DisappearingBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  width: 16,
  height: 16,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  border: `2px solid ${theme.palette.mode === "dark" ? alpha("#0a0e13", 1) : "#fff"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiSvgIcon-root": { fontSize: "9px", color: "#fff" },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: { xs: 46, sm: 52 },
  height: { xs: 46, sm: 52 },
  fontSize: { xs: "18px", sm: "21px" },
  fontWeight: 700,
  background: theme.palette.mode === "dark" ? "#2a2a2a" : "#ffffff",
  color: theme.palette.mode === "dark" ? "#ccc" : "#555",
  border: `2px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.1)}`,
  boxShadow: `0 2px 8px ${alpha("#000", 0.1)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",

  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const UserDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  paddingLeft: "10px"
});

const Username = styled(Typography)(({ theme }) => ({
  fontSize: { xs: "16px", sm: "18px" },
  fontWeight: 700,
  color: theme.palette.mode === "dark" ? "#ffffff" : "#1a1a1a",
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textShadow: theme.palette.mode === "dark"
    ? `0 2px 8px ${alpha("#000", 0.3)}`
    : `0 1px 2px ${alpha("#000", 0.1)}`,
  cursor: "pointer",
  transition: "color 0.2s ease",

  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const StatusText = styled(Typography)(({ theme, isOnline }) => ({
  fontSize: { xs: "12px", sm: "13px" },
  fontWeight: 500,
  color: isOnline
    ? "#4CAF50"
    : theme.palette.mode === "dark"
    ? alpha("#fff", 0.55)
    : alpha("#000", 0.6),
  letterSpacing: "0.01em",
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  transition: "color 0.3s ease",
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.7) : alpha("#000", 0.6),
  padding: "10px",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    transform: "scale(1.05)",
  },

  "&:active": {
    transform: "scale(0.95)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: { xs: "22px", sm: "24px" },
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    background: theme.palette.mode === "dark"
      ? "rgba(28, 28, 32, 0.82)"
      : "rgba(255, 255, 255, 0.78)",
    backgroundImage: "none",
    backdropFilter: "blur(40px) saturate(180%)",
    WebkitBackdropFilter: "blur(40px) saturate(180%)",
    borderRadius: "14px",
    border: `1px solid ${theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.10)"
      : "rgba(0,0,0,0.09)"}`,
    boxShadow: theme.palette.mode === "dark"
      ? `0 8px 32px rgba(0,0,0,0.55)`
      : `0 8px 32px rgba(0,0,0,0.18)`,
    minWidth: "220px",
    padding: "8px",
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  borderRadius: "8px",
  padding: "10px 14px",
  margin: "2px 0",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.88)",
  transition: "all 0.2s ease",

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)",
  },

  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
  },

  "&.danger": {
    color: theme.palette.error.main,
    
    "& .MuiSvgIcon-root": {
      color: theme.palette.error.main,
    },

    "&:hover": {
      background: alpha(theme.palette.error.main, 0.1),
    },
  },
}));

const MenuSectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
  padding: "8px 14px 4px",
}));

const MenuDivider = styled(Divider)(({ theme }) => ({
  margin: "6px 0",
  borderColor: theme.palette.mode === "dark"
    ? alpha("#fff", 0.06)
    : alpha("#000", 0.06),
}));

// Avatar Zoom Modal Styles - Simple & Clean
const AvatarModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
});

const ModalBackdrop = styled(Backdrop)(() => ({
  backdropFilter: "blur(8px)",
  backgroundColor: alpha("#000", 0.75),
}));

const ModalContent = styled(Box)({
  position: "relative",
  outline: "none",
  textAlign: "center",
  animation: "zoomIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "@keyframes zoomIn": {
    from: {
      opacity: 0,
      transform: "scale(0.8)",
    },
    to: {
      opacity: 1,
      transform: "scale(1)",
    },
  },
});

const ZoomedAvatar = styled(Avatar)(({ theme }) => ({
  width: { xs: "280px", sm: "420px" },
  height: { xs: "280px", sm: "420px" },
  fontSize: { xs: "120px", sm: "180px" },
  fontWeight: 700,
  background: `linear-gradient(135deg,
    ${theme.palette.primary.main},
    ${theme.palette.primary.dark})`,
  border: `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  boxShadow: `0 20px 60px ${alpha("#000", 0.5)}`,
  margin: "0 auto",
}));

const CloseModalButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  right: "20px",
  color: "#fff",
  backgroundColor: alpha("#000", 0.5),
  width: "48px",
  height: "48px",
  transition: "all 0.3s ease",
  zIndex: 10001,

  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.9),
    transform: "scale(1.1) rotate(90deg)",
  },

  "&:active": {
    transform: "scale(0.95) rotate(90deg)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "28px",
  },
}));

const UserInfo = styled(Box)({
  marginTop: "20px",
  textAlign: "center",
});

const ModalUsername = styled(Typography)({
  fontSize: { xs: "24px", sm: "28px" },
  fontWeight: 700,
  marginBottom: "8px",
  color: "#fff",
  textShadow: `0 2px 8px ${alpha("#000", 0.5)}`,
});

const ModalStatus = styled(Typography)({
  fontSize: { xs: "14px", sm: "16px" },
  fontWeight: 500,
  color: alpha("#fff", 0.85),
  textShadow: `0 1px 4px ${alpha("#000", 0.4)}`,
});

export default function ChatHeader({ otherUser, onSearchClick, hasConnectedBefore, messageCount = 0 }) {
  const history = useHistory();
  const {
    switchActiveChat,
    appState,
    muteConversationApiCall,
    archiveConversationApiCall,
    softDeleteConversationApiCall,
    checkBlockStatusApiCall,
    blockUserApiCall,
    unblockUserApiCall,
    reportUserApiCall,
    showSnackbar,
  } = useApp();
  const { activeChat } = appState;
  const isOnline = otherUser?.is_online === 1;
  const username = otherUser?.username || "User";
  const firstLetter = username.charAt(0).toUpperCase();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuButtonRef = useRef(null);
  const [disappearingInterval, setDisappearingInterval] = useState(
    () => activeChat?.disappearing_interval || 0
  );
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const isDisappearingOn = disappearingInterval > 0;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const isMuted = !!activeChat?.is_muted;
  const isArchived = !!activeChat?.is_archived;

  const shouldShowLastSeen = hasConnectedBefore || messageCount > 0 || activeChat?.has_connected_before;

  const [statusText, setStatusText] = useState("");

  // Fetch block status whenever the chat partner changes
  useEffect(() => {
    if (!otherUser?.id) { setIsBlocked(false); return; }
    checkBlockStatusApiCall(otherUser.id).then(res => setIsBlocked(res.isBlocked));
  }, [otherUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep disappearing indicator in sync: update when chat changes or other user toggles it
  useEffect(() => {
    setDisappearingInterval(activeChat?.disappearing_interval || 0);
  }, [activeChat?.id, activeChat?.disappearing_interval]);

  useEffect(() => {
    const roomId = activeChat?.id;
    if (!roomId) return;
    const handler = (e) => {
      if (e.detail?.roomId?.toString() === roomId?.toString()) {
        setDisappearingInterval(e.detail.interval ?? 0);
      }
    };
    window.addEventListener("iota:disappearing_updated", handler);
    return () => window.removeEventListener("iota:disappearing_updated", handler);
  }, [activeChat?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackClick = () => {
    if (settingsOpen) {
      setSettingsOpen(false);
      // Reopen the options menu so the user lands back on it (acts like pressing the menu button)
      setAnchorEl(menuButtonRef.current);
      return;
    }
    switchActiveChat({});
    history.push("/messages");
  };

  const handleViewProfile = () => {
    if (otherUser?.username) {
      history.push(`/${otherUser.username}`);
    }
    handleMenuClose();
  };

  const handleSearchClick = () => {
    if (onSearchClick) onSearchClick();
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAvatarClick = () => setAvatarModalOpen(true);
  const handleAvatarModalClose = () => setAvatarModalOpen(false);

  const handleUsernameClick = () => {
    if (otherUser?.username) history.push(`/${otherUser.username}`);
  };

  const handleMute = async () => {
    handleMenuClose();
    if (!activeChat?.id) return;
    await muteConversationApiCall(activeChat.id);
  };

  const handleArchive = async () => {
    handleMenuClose();
    if (!activeChat?.id) return;
    await archiveConversationApiCall(activeChat.id);
  };

  const handleDeleteConversation = async () => {
    setDeleteDialogOpen(false);
    if (!activeChat?.id) return;
    const result = await softDeleteConversationApiCall(activeChat.id);
    if (result?.success) {
      switchActiveChat({});
      history.push("/messages");
    }
  };

  const handleBlock = async () => {
    setBlockDialogOpen(false);
    if (!otherUser?.id) return;
    const result = await blockUserApiCall(otherUser.id, otherUser.username, activeChat?.id);
    if (result?.success) setIsBlocked(true);
  };

  const handleUnblock = async () => {
    if (!otherUser?.id) return;
    const result = await unblockUserApiCall(otherUser.id, otherUser.username, activeChat?.id);
    if (result?.success) setIsBlocked(false);
    handleMenuClose();
  };

  const handleReport = async () => {
    setReportDialogOpen(false);
    if (!otherUser?.id) return;
    await reportUserApiCall(otherUser.id, reportReason.trim());
    setReportReason("");
  };

  useEffect(() => {
    if (shouldShowLastSeen) {
      // Get the formatted time and add "last seen " prefix if needed
      const formattedTime = formatLastSeen(otherUser?.last_seen, isOnline);
      const displayText = formattedTime === "online" || formattedTime === "offline"
        ? formattedTime
        : `last seen ${formattedTime}`;

      setStatusText(displayText);

      let intervalId;

      if (!isOnline && otherUser?.last_seen) {
        const now = new Date();
        const lastSeen = new Date(otherUser.last_seen);
        const diffMs = now - lastSeen;
        const diffMinutes = Math.floor(diffMs / 60000);

        let updateInterval;
        if (diffMinutes < 1) {
          updateInterval = 5000;
        } else if (diffMinutes < 60) {
          updateInterval = 30000;
        } else {
          updateInterval = 60000;
        }

        // Update periodically
        intervalId = setInterval(() => {
          const updatedTime = formatLastSeen(otherUser.last_seen, false);
          const updatedText = updatedTime === "offline" ? updatedTime : `last seen ${updatedTime}`;
          setStatusText(updatedText);
        }, updateInterval);
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    } else {
      setStatusText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherUser?.last_seen, isOnline, shouldShowLastSeen]);

  return (
    <>
      <HeaderContainer>
        <LeftSection>
          <BackButton onClick={handleBackClick} aria-label="Back to chat list">
            <ArrowBack />
          </BackButton>

          <AvatarWrapper isOnline={isOnline} onClick={handleAvatarClick}>
            <StyledAvatar src={otherUser?.profile_image_url || otherUser?.profile_pic || otherUser?.avatar}>
              {firstLetter}
            </StyledAvatar>
            {isDisappearingOn && (
              <DisappearingBadge title="Disappearing messages on">
                <TimerIcon />
              </DisappearingBadge>
            )}
          </AvatarWrapper>

          <UserDetails>
            <Username onClick={handleUsernameClick}>
              {capitalizeEachFirstWord(username)}
            </Username>
            {statusText && (
              <StatusText isOnline={isOnline}>
                {statusText}
              </StatusText>
            )}
          </UserDetails>
        </LeftSection>

        <RightSection>
          <SearchButton onClick={handleSearchClick} aria-label="Search messages">
            <SearchIcon />
          </SearchButton>

          <MenuButton
            ref={menuButtonRef}
            aria-label="More options"
            aria-controls={open ? "chat-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleMenuClick}
          >
            <MoreVert />
          </MenuButton>
        </RightSection>
      </HeaderContainer>

      <StyledMenu
        id="chat-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuSectionLabel>Actions</MenuSectionLabel>
        <StyledMenuItem onClick={() => { handleSearchClick(); handleMenuClose(); }}>
          <SearchIcon />
          Search in chat
        </StyledMenuItem>
        <StyledMenuItem onClick={handleViewProfile}>
          <PersonIcon />
          View profile
        </StyledMenuItem>
        
        <MenuDivider />
        
        <MenuSectionLabel>Preferences</MenuSectionLabel>
        <StyledMenuItem onClick={handleMute}>
          {isMuted ? <UnmuteIcon /> : <MuteIcon />}
          {isMuted ? "Unmute notifications" : "Mute notifications"}
        </StyledMenuItem>
        <StyledMenuItem onClick={handleArchive}>
          {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
          {isArchived ? "Unarchive chat" : "Archive chat"}
        </StyledMenuItem>
        <StyledMenuItem onClick={() => { handleMenuClose(); setSettingsOpen(true); }}>
          <SettingsIcon />
          Chat settings
        </StyledMenuItem>

        <MenuDivider />

        <MenuSectionLabel>Danger Zone</MenuSectionLabel>
        {isBlocked ? (
          <StyledMenuItem onClick={handleUnblock}>
            <BlockIcon />
            Unblock user
          </StyledMenuItem>
        ) : (
          <StyledMenuItem className="danger" onClick={() => { handleMenuClose(); setBlockDialogOpen(true); }}>
            <BlockIcon />
            Block user
          </StyledMenuItem>
        )}
        <StyledMenuItem className="danger" onClick={() => { handleMenuClose(); setReportDialogOpen(true); }}>
          <ReportIcon />
          Report user
        </StyledMenuItem>
        <StyledMenuItem className="danger" onClick={() => { handleMenuClose(); setDeleteDialogOpen(true); }}>
          <DeleteIcon />
          Delete conversation
        </StyledMenuItem>
      </StyledMenu>

      {/* Avatar Zoom Modal */}
      <AvatarModal
        open={avatarModalOpen}
        onClose={handleAvatarModalClose}
        closeAfterTransition
        slots={{ backdrop: ModalBackdrop }}
      >
        <>
          <CloseModalButton onClick={handleAvatarModalClose} aria-label="Close">
            <CloseIcon />
          </CloseModalButton>
          <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={handleAvatarModalClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ZoomedAvatar src={otherUser?.profile_image_url || otherUser?.profile_pic || otherUser?.avatar}>
                {firstLetter}
              </ZoomedAvatar>
              <UserInfo>
                <ModalUsername>{capitalizeEachFirstWord(username)}</ModalUsername>
                {shouldShowLastSeen && statusText && (
                  <ModalStatus>{statusText}</ModalStatus>
                )}
              </UserInfo>
            </ModalContent>
          </Box>
        </>
      </AvatarModal>

      {/* Block confirmation dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Block {capitalizeEachFirstWord(username)}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            They won't be able to message you and you won't see their messages.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setBlockDialogOpen(false)} variant="outlined" sx={{ borderRadius: "8px" }}>Cancel</Button>
          <Button onClick={handleBlock} variant="contained" color="error" sx={{ borderRadius: "8px" }}>Block</Button>
        </DialogActions>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} PaperProps={{ sx: { borderRadius: "16px", p: 1, minWidth: 320 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Report {capitalizeEachFirstWord(username)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setReportDialogOpen(false)} variant="outlined" sx={{ borderRadius: "8px" }}>Cancel</Button>
          <Button onClick={handleReport} variant="contained" color="error" sx={{ borderRadius: "8px" }}>Report</Button>
        </DialogActions>
      </Dialog>

      {/* Delete conversation confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete conversation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            This will hide the conversation from your list. The other person's copy is unaffected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" sx={{ borderRadius: "8px" }}>Cancel</Button>
          <Button onClick={handleDeleteConversation} variant="contained" color="error" sx={{ borderRadius: "8px" }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <ChatSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onGoBack={handleBackClick}
        roomId={activeChat?.id}
        otherUserId={otherUser?.id}
      />
    </>
  );
}
